import {convertFromDraftJsToHtml} from './DraftJsToHtmlConverter';
import {convertFromHtmlToDraftJs} from './HtmlToDraftJsConverter';

export type RoundTripDrift = {
	/** Distinct block types at the positions that changed, from both sides. */
	blockTypes: string[];
	blockCountBefore: number;
	blockCountAfter: number;
	entityTypesBefore: string[];
	entityTypesAfter: string[];
};

type DraftBlock = {
	type?: string;
	key?: string;
	text?: string;
	depth?: number;
	inlineStyleRanges?: unknown[];
	entityRanges?: {offset?: number; length?: number}[];
	data?: Record<string, unknown>;
};
type DraftDocument = {blocks?: DraftBlock[]; entityMap?: Record<string, {type?: string} | null> | null};

/** One open-and-blur cycle: what the stored content becomes if the user changes nothing. */
export type RoundTrip = (storedDraftJs: string) => string;

// Reporting is off on both legs: this is a synthetic round trip of content the user
// never edited, so a throw here is a diagnostic result (swallowed below), not a
// conversion the user witnessed. Left on, it would file Sentry events indistinguishable
// from real save and render failures.
const defaultRoundTrip: RoundTrip = storedDraftJs =>
	convertFromHtmlToDraftJs(convertFromDraftJsToHtml(storedDraftJs, undefined, {report: false}), {report: false});

/**
 * A block reduced to the parts a round trip is supposed to preserve.
 *
 * Three things differ between the two sides for reasons that are not drift, and
 * comparing the raw JSON would report all of them:
 *
 *   - **Block keys** are regenerated by every conversion.
 *   - **Property order** is not stable — the stored form and `convertToRaw`'s output
 *     serialise the same fields in a different order.
 *   - **`data`** is absent on the stored side and an empty object on the way back.
 *
 * Reading named fields in a fixed order rather than stringifying whatever arrived is
 * what keeps all three out. `entityRanges` is reduced to its offsets and lengths
 * because the entity keys they point at are renumbered by the round trip; the entity
 * *types* are compared separately, which is the part that actually matters.
 */
const canonicalBlock = (block: DraftBlock | undefined): string =>
	JSON.stringify({
		type: block?.type ?? null,
		text: block?.text ?? '',
		depth: block?.depth ?? 0,
		inlineStyleRanges: block?.inlineStyleRanges ?? [],
		entityRanges: (block?.entityRanges ?? []).map(range => [range?.offset, range?.length]),
		data: block?.data && Object.keys(block.data).length > 0 ? block.data : {},
	});

const parseDocument = (content: string): DraftDocument | null => {
	try {
		const parsed = JSON.parse(content) as DraftDocument;
		return Array.isArray(parsed?.blocks) ? parsed : null;
	} catch {
		return null;
	}
};

const distinctSorted = (values: unknown[]): string[] =>
	[...new Set(values.filter((value): value is string => typeof value === 'string' && value !== ''))].sort();

const entityTypesOf = (document: DraftDocument): string[] =>
	distinctSorted(Object.values(document.entityMap ?? {}).map(entity => entity?.type));

/**
 * Block types at positions where the two documents disagree, from both sides.
 *
 * Both sides, because the interesting case is a substitution — a `code-block`
 * arriving back as `unstyled` — and naming only one of them describes half the bug.
 */
const changedBlockTypes = (before: DraftBlock[], after: DraftBlock[]): string[] => {
	const types: (string | undefined)[] = [];
	for (let i = 0; i < Math.max(before.length, after.length); i++) {
		const beforeBlock = before[i];
		const afterBlock = after[i];
		if (canonicalBlock(beforeBlock) === canonicalBlock(afterBlock)) continue;
		types.push(beforeBlock?.type, afterBlock?.type);
	}
	return distinctSorted(types);
};

/**
 * Whether stored content survives one open-and-blur cycle unchanged, and what changes if not.
 *
 * Every task open runs DraftJS to HTML and every blur runs HTML back to DraftJS —
 * **even when nothing was typed**. Any construct the two translators do not agree on
 * therefore degrades a little on each cycle: tables losing borders, indents
 * flattening, code blocks becoming plain text. None of that crashes and none of it
 * raises an error, so it produces complaints with no corresponding events at all,
 * which is why it needs a detector of its own.
 *
 * This re-serialises the **stored** content rather than comparing against what the
 * user left in the editor, so it needs no way to tell an edit from a degradation: it
 * measures the property directly — does this content survive a round trip — and the
 * answer is the same whether or not the user typed.
 *
 * It detects and characterises drift; it does not prevent it. Turning the flags back
 * off does not restore content that has already round-tripped, which is what makes
 * this worth having before a rollout rather than after one.
 *
 * Returns null when the content survives, is not DraftJS, or cannot be converted at
 * all — a converter throw is already reported by the converter with better context,
 * and reporting it again here would double-count it.
 */
export const detectRoundTripDrift = (
	storedDraftJs: string | null | undefined,
	roundTrip: RoundTrip = defaultRoundTrip
): RoundTripDrift | null => {
	if (!storedDraftJs) return null;

	const before = parseDocument(storedDraftJs);
	if (!before) return null;

	let after: DraftDocument | null;
	try {
		after = parseDocument(roundTrip(storedDraftJs));
	} catch {
		return null;
	}
	if (!after) return null;

	const beforeBlocks = before.blocks ?? [];
	const afterBlocks = after.blocks ?? [];
	const blockTypes = changedBlockTypes(beforeBlocks, afterBlocks);
	const entityTypesBefore = entityTypesOf(before);
	const entityTypesAfter = entityTypesOf(after);

	const entitiesChanged = JSON.stringify(entityTypesBefore) !== JSON.stringify(entityTypesAfter);
	if (blockTypes.length === 0 && !entitiesChanged) return null;

	return {
		blockTypes,
		blockCountBefore: beforeBlocks.length,
		blockCountAfter: afterBlocks.length,
		entityTypesBefore,
		entityTypesAfter,
	};
};
